import { Injectable, inject } from '@angular/core';
import { invoke, isTauri } from '@tauri-apps/api/core';
import { TimelineTask } from '../models/activity.model';
import { BehaviorSubject } from 'rxjs';
import { UserService } from './user.service';
import { Task } from '../models/task.model';

@Injectable({
  providedIn: 'root',
})

export class TauriService {
  private userService = inject(UserService);
  private timelineTaskSubject = new BehaviorSubject<TimelineTask[]>([]);
  timelineTasks$ = this.timelineTaskSubject.asObservable();

  private rawTasksCache: Task[] | null = null;
  private currentSecondsToAvoid = 5;

  constructor(){
    this.userService.settings$.subscribe(settings => {
      if (settings && settings.secondsToAvoid) this.currentSecondsToAvoid = parseInt(settings.secondsToAvoid, 10);
      else this.currentSecondsToAvoid = 5;
      if (this.rawTasksCache) this.processAndEmitTasks(this.rawTasksCache);
      else this.getTasksFromDb();
    });
  }

  async getTasksFromDb() {
    // Only call Tauri APIs if running in Tauri environment
    if (!isTauri()) {
      const fakeData = this.getFakeTasks();
      this.timelineTaskSubject.next(
        fakeData.filter((t: TimelineTask) => t.duration * 60 > this.currentSecondsToAvoid)
      );
      return;
    }

    try {
      const rawData = await invoke<any[]>('get_tasks');
      this.rawTasksCache = rawData;
      this.processAndEmitTasks(rawData);
    } catch (err) {
      console.error(err);
      const fakeData = this.getFakeTasks();
      this.timelineTaskSubject.next(
        fakeData.filter((t: TimelineTask) => t.duration * 60 > this.currentSecondsToAvoid)
      );
    }
  }

  async getTasksForDate(date: Date) {
    console.log('🟢 [TS] getTasksForDate called for date:', date.toISOString());

    if (!isTauri()) {
      console.log('🟡 [TS] Not in Tauri environment, using fake data');
      const fakeData = this.getFakeTasksForDate(date);
      console.log('🟢 [TS] Generated', fakeData.length, 'fake tasks');
      this.timelineTaskSubject.next(
        fakeData.filter((t: TimelineTask) => t.duration * 60 > this.currentSecondsToAvoid)
      );
      return;
    }

    try {
      // Convertir la date en timestamp (début du jour)
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const timestamp = Math.floor(startOfDay.getTime() / 1000);

      console.log('🟢 [TS] Calling Rust get_tasks_by_date with timestamp:', timestamp);
      console.log('🟢 [TS] Date range:', startOfDay.toISOString(), 'to', new Date(startOfDay.getTime() + 24*60*60*1000).toISOString());

      const rawData = await invoke<any[]>('get_tasks_by_date', { dateTimestamp: timestamp });

      console.log('✅ [TS] Received', rawData.length, 'raw tasks from Rust');
      if (rawData.length > 0) {
        console.log('🟢 [TS] First task sample:', {
          id: rawData[0].id,
          program: rawData[0].program,
          title: rawData[0].title,
          start: rawData[0].start,
          startDate: new Date(rawData[0].start * 1000).toISOString()
        });
      }

      this.rawTasksCache = rawData;
      this.processAndEmitTasks(rawData);
    } catch (err) {
      console.error('❌ [TS] Error loading tasks for date:', err);
      console.error('❌ [TS] Error details:', JSON.stringify(err, null, 2));
      const fakeData = this.getFakeTasksForDate(date);
      console.log('🟡 [TS] Falling back to', fakeData.length, 'fake tasks');
      this.timelineTaskSubject.next(
        fakeData.filter((t: TimelineTask) => t.duration * 60 > this.currentSecondsToAvoid)
      );
    }
  }

  private processAndEmitTasks(rawData: any[]) {
    console.log('🟢 [TS] processAndEmitTasks called with', rawData.length, 'raw tasks');
    console.log('🟢 [TS] Filtering tasks with duration >', this.currentSecondsToAvoid, 'seconds');

    const task = rawData.map(item => {
      const start = new Date(item.start * 1000);
      const end = new Date(item.end * 1000);
      const duration = Math.round((end.getTime() - start.getTime()) / 1000 );
      return {
        id: item.id,
        name: item.program,
        title: item.title,
        type: item.type_,
        startTime: start,
        endTime: end,
        duration: duration
      };
    }).filter(t => t.duration > this.currentSecondsToAvoid);

    console.log('✅ [TS] Processed and filtered to', task.length, 'tasks');
    if (task.length > 0) {
      console.log('🟢 [TS] First processed task:', {
        id: task[0].id,
        name: task[0].name,
        title: task[0].title,
        duration: task[0].duration + 's',
        startTime: task[0].startTime.toISOString()
      });
    }

    console.log('🟢 [TS] About to emit', task.length, 'tasks to BehaviorSubject');
    this.timelineTaskSubject.next(task);
    console.log('✅ [TS] Tasks emitted to BehaviorSubject - current subscribers will be notified');
  }

  resetState() {
    this.timelineTaskSubject.next([]);
    this.rawTasksCache = null;
    this.currentSecondsToAvoid = 5;
  }

  private getFakeTasks(): TimelineTask[] {
    // Utiliser la date du jour pour générer les fake tasks
    return this.getFakeTasksForDate(new Date());
  }

  private getFakeTasksForDate(date: Date): TimelineTask[] {
    // Créer des fake tasks répartis tout au long de la journée demandée
    const dayStart = new Date(date);
    dayStart.setHours(6, 0, 0, 0); // Commencer à 6h du matin
    const baseTime = dayStart.getTime();

    return [
      {
        id: 1,
        name: "chrome.exe",
        title: "StackOverflow - Reading documentation",
        type: "browser",
        startTime: new Date(baseTime), // 6:00
        endTime: new Date(baseTime + 25 * 60 * 1000), // 6:25
        duration: 25 * 60,
      },
      {
        id: 2,
        name: "code.exe",
        title: "project1/main.ts",
        type: "editor",
        startTime: new Date(baseTime + 30 * 60 * 1000), // 6:30
        endTime: new Date(baseTime + 90 * 60 * 1000), // 7:30
        duration: 60 * 60,
      },
      {
        id: 3,
        name: "slack.exe",
        title: "Team standup meeting",
        type: "communication",
        startTime: new Date(baseTime + 120 * 60 * 1000), // 8:00
        endTime: new Date(baseTime + 150 * 60 * 1000), // 8:30
        duration: 30 * 60,
      },
      {
        id: 4,
        name: "code.exe",
        title: "project2/components/header.tsx",
        type: "editor",
        startTime: new Date(baseTime + 180 * 60 * 1000), // 9:00
        endTime: new Date(baseTime + 240 * 60 * 1000), // 10:00
        duration: 60 * 60,
      },
      {
        id: 5,
        name: "chrome.exe",
        title: "GitHub - Reviewing pull requests",
        type: "browser",
        startTime: new Date(baseTime + 240 * 60 * 1000), // 10:00
        endTime: new Date(baseTime + 270 * 60 * 1000), // 10:30
        duration: 30 * 60,
      },
      {
        id: 6,
        name: "zoom.exe",
        title: "Client meeting - Project review",
        type: "communication",
        startTime: new Date(baseTime + 300 * 60 * 1000), // 11:00
        endTime: new Date(baseTime + 360 * 60 * 1000), // 12:00
        duration: 60 * 60,
      },
      {
        id: 7,
        name: "spotify.exe",
        title: "Focus playlist",
        type: "music",
        startTime: new Date(baseTime + 420 * 60 * 1000), // 13:00
        endTime: new Date(baseTime + 480 * 60 * 1000), // 14:00
        duration: 60 * 60,
      },
      {
        id: 8,
        name: "code.exe",
        title: "project1/services/api.ts",
        type: "editor",
        startTime: new Date(baseTime + 480 * 60 * 1000), // 14:00
        endTime: new Date(baseTime + 540 * 60 * 1000), // 15:00
        duration: 60 * 60,
      },
      {
        id: 9,
        name: "chrome.exe",
        title: "Stack Overflow - Debugging CORS issue",
        type: "browser",
        startTime: new Date(baseTime + 540 * 60 * 1000), // 15:00
        endTime: new Date(baseTime + 570 * 60 * 1000), // 15:30
        duration: 30 * 60,
      },
      {
        id: 10,
        name: "slack.exe",
        title: "Dev team discussion",
        type: "communication",
        startTime: new Date(baseTime + 600 * 60 * 1000), // 16:00
        endTime: new Date(baseTime + 630 * 60 * 1000), // 16:30
        duration: 30 * 60,
      },
      {
        id: 11,
        name: "code.exe",
        title: "project3/backend/server.go",
        type: "editor",
        startTime: new Date(baseTime + 660 * 60 * 1000), // 17:00
        endTime: new Date(baseTime + 720 * 60 * 1000), // 18:00
        duration: 60 * 60,
      },
      {
        id: 12,
        name: "chrome.exe",
        title: "Gmail - Responding to emails",
        type: "browser",
        startTime: new Date(baseTime + 720 * 60 * 1000), // 18:00
        endTime: new Date(baseTime + 750 * 60 * 1000), // 18:30
        duration: 30 * 60,
      },
      {
        id: 13,
        name: "code.exe",
        title: "project1/bug-fixes.ts",
        type: "editor",
        startTime: new Date(baseTime + 780 * 60 * 1000), // 19:00
        endTime: new Date(baseTime + 840 * 60 * 1000), // 20:00
        duration: 60 * 60,
      },
      {
        id: 14,
        name: "slack.exe",
        title: "Quick check-in with team",
        type: "communication",
        startTime: new Date(baseTime + 840 * 60 * 1000), // 20:00
        endTime: new Date(baseTime + 855 * 60 * 1000), // 20:15
        duration: 15 * 60,
      },
      {
        id: 15,
        name: "chrome.exe",
        title: "Reading technical articles",
        type: "browser",
        startTime: new Date(baseTime + 870 * 60 * 1000), // 20:30
        endTime: new Date(baseTime + 930 * 60 * 1000), // 21:30
        duration: 60 * 60,
      },
      {
        id: 16,
        name: "spotify.exe",
        title: "Evening chill playlist",
        type: "music",
        startTime: new Date(baseTime + 930 * 60 * 1000), // 21:30
        endTime: new Date(baseTime + 960 * 60 * 1000), // 22:00
        duration: 30 * 60,
      },
    ];
  }
}
